/**
 * How a page is fitted into a reader.
 *
 * Pure arithmetic about a rectangle, deliberately: this is the part of the reader
 * a reader is unusable without — a page half a pixel wider than its box is a page
 * with a scrollbar — and it is also the part that can be checked without a
 * browser. A wide window fits the page to its height and a narrow one to its
 * width, and only one of those shapes is ever on screen at a time, so the rule
 * lives here, where both can be held still and asserted.
 */

/**
 * The size of every round control in the reader.
 *
 * Here rather than in the surface that draws them because it is a measurement that
 * surface reads: the header's controls and the pager in the bar are one button, and
 * they are all this wide.
 */
export const READER_CONTROL_SIZE = 36

/**
 * How much of a scroll area its own bar takes.
 *
 * The bars are drawn by `pdfStyles.web.ts` rather than left to the platform —
 * pages are read across their full width, and a bar that appears as a page grows
 * should not also shove the text sideways. Styling it commits us to a *classic*
 * bar on Chrome, which takes room out of the box it scrolls; whether it does here
 * is the platform's business (overlay bars take none), and a document surface
 * measures the box's *parent*, which counts the bar either way. So this is the
 * space to leave for it in the frame before anything has been measured, and the
 * stylesheet reads it too, so the fit and the declared bar cannot disagree.
 */
export const PDF_SCROLLBAR_WIDTH = 10

/**
 * A page wider than this in a continuous column is a wall, not a page. Only the
 * scrolling view needs the cap: a paged fit is bounded by the reader's height.
 */
const SCROLL_MAX_PAGE_WIDTH = 1000

/**
 * Below this the reader is unmeasurable rather than small: it has not laid out
 * yet, and a page a few pixels wide would be a flash of nonsense on the way in.
 */
const MIN_PAGE_SIDE = 120

/**
 * A page's size in the paged view: as large as the area allows.
 *
 * The whole area is the budget — there is no frame around the paper, because on a
 * phone a frame is a seventh of the width of a worksheet, which is the difference
 * between legible and not. Nothing has to be taken out of it for controls: the
 * reader's own are not drawn on the stage, and its pager lives in the bar below.
 *
 * Whichever of the two dimensions binds is filled *exactly*, and the other
 * follows from the page's shape: a page fitted to the bottom of a tall window
 * should touch the bottom of it, not stop two pixels above it because the width
 * was rounded first. The dimension that does not bind is rounded down rather than
 * to nearest, deliberately — half a pixel of page wider than the box it is in is
 * a page with a scrollbar, and a page exactly as wide as the box is not.
 */
export function fitPagedPage({
  width,
  height,
  ratio,
  zoom,
}: {
  /** The area available, measured. */
  width: number
  height: number
  /** The page's shape: width over height. */
  ratio: number
  /** A multiple of the fit, so 1 means "exactly as large as it fits". */
  zoom: number
}): {pageWidth: number; pageHeight: number} {
  const areaWidth = Math.max(width, MIN_PAGE_SIDE)
  const areaHeight = Math.max(height, MIN_PAGE_SIDE)
  const widthBinds = areaWidth <= areaHeight * ratio
  const pageWidth = Math.max(
    1,
    Math.floor((widthBinds ? areaWidth : areaHeight * ratio) * zoom),
  )
  const pageHeight = widthBinds
    ? Math.max(1, Math.floor(pageWidth / ratio))
    : Math.max(1, Math.floor(areaHeight * zoom))
  return {pageWidth, pageHeight}
}

/**
 * How wide the paper is in a paged reader of this size — the column a card drawn
 * *over* a page is as wide as.
 *
 * The card an author writes a page's audio and questions in is about that page, so
 * it takes the paper's own width rather than the reader's. On a phone the two are
 * the same number — the page is fitted to the screen — which is why the card
 * already looked right there; on a laptop in full screen they are 450 and 1280,
 * and a card that took the second was a sheet of paper the width of the room.
 *
 * Capped by the area itself: past 100% the page is deliberately larger than the
 * reader — the reader is inspecting a line and pans it — and a card that grew to
 * match would have its own edges off screen. An area not measured yet is the same
 * floor `fitPagedPage` uses, so the column is never a sliver either.
 */
export function fitPageColumn({
  width,
  height,
  ratio,
  zoom,
}: {
  /** The area the page is fitted into, measured. */
  width: number
  height: number
  /** The page's shape: width over height. */
  ratio: number
  /** A multiple of the fit, so 1 means "exactly as large as it fits". */
  zoom: number
}): number {
  const area = Math.max(width, MIN_PAGE_SIDE)
  return Math.min(fitPagedPage({width, height, ratio, zoom}).pageWidth, area)
}

/**
 * The box a page's thumbnail is drawn in.
 *
 * A thumbnail is a page, so it is the page's own shape: the rail gives it the
 * width it has room for and the height follows from `ratio`. It used to be drawn
 * into a square — the rail passed its own width as both dimensions — and the
 * canvas blits the page to fill its box whatever shape that box is, so a
 * landscape worksheet came out squeezed into a square tile and a portrait one was
 * stretched into the same square. The rail is the only surface in the reader that
 * did this; the carousel and the stage below already fit the page by its ratio.
 *
 * A shape that cannot be read — zero, negative, or not a number at all, any of
 * which a malformed page's viewport can produce — falls back to the square rather
 * than to a guess at the shape.
 */
export function fitPageThumb({
  width,
  ratio,
}: {
  /** The width the rail has room for. */
  width: number
  /** The page's shape: width over height, as everywhere else here. */
  ratio: number
}): {width: number; height: number} {
  const thumbWidth = Math.max(1, Math.round(width))
  const shape = Number.isFinite(ratio) && ratio > 0 ? ratio : 1
  return {
    width: thumbWidth,
    height: Math.max(1, Math.round(thumbWidth / shape)),
  }
}

/**
 * A page's size in the continuous view, where only the width is a budget: the
 * column is as long as the document, and the reader scrolls it.
 *
 * `width` is the space the page may actually have — the column's own content
 * box, which the scrolling view measures for itself rather than assuming, because
 * whether a scrollbar takes room out of a box is the platform's business (see
 * `PDF_SCROLLBAR_WIDTH` for that first, unmeasured frame).
 */
export function fitScrollPage({
  width,
  ratio,
  zoom,
}: {
  width: number
  ratio: number
  zoom: number
}): {pageWidth: number; pageHeight: number} {
  const pageWidth = Math.max(
    1,
    Math.floor(
      Math.min(Math.max(width, MIN_PAGE_SIDE), SCROLL_MAX_PAGE_WIDTH) * zoom,
    ),
  )
  const pageHeight = Math.max(1, Math.floor(pageWidth / ratio))
  return {pageWidth, pageHeight}
}
